import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteHook } from '@shared/hooks';
import { AmountUtils, cn, ColorUtils, DateUtils, PermissionUtils, RoutePaths } from '@shared/utils';
import {
  DepositPaymentDirectionEnum,
  DepositPaymentTypeOfTransferEnum,
  TransactionDataType,
  TransactionDirection,
  type TransactionsWithPaymentsResponse,
} from '@shared/api';
import { Table, TableCellType } from '@shared/widgets';

interface PaymentTableProps {
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  data: TransactionsWithPaymentsResponse[];
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
}

export const PaymentTable = (props: PaymentTableProps) => {
  const { navigate } = useRouteHook();

  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      t('tables.payment.columns.status'),
      t('tables.payment.columns.direction'),
      t('tables.payment.columns.date'),
      t('tables.payment.columns.referenceNo'),
      t('tables.payment.columns.amount'),
      t('tables.payment.columns.currency'),
      t('tables.payment.columns.to'),
      t('tables.payment.columns.from'),
      t('tables.payment.columns.transactionReference'),
      t('tables.payment.columns.totalAmount'),
    ],
    [t],
  );
  return (
    <Table
      isScrollable
      isLoading={props.isLoading}
      error={props.error}
      onRefresh={props.onRefresh}
      columns={columns}
      items={props.data.map((row) => {
        if (row.dataType === TransactionDataType.Payment) {
          const payment = row.payment;
          const color = ColorUtils.getDepositPaymentStatusStyles(payment.status);

          let amount = 0;
          if (payment.typeOfTransfer === DepositPaymentTypeOfTransferEnum.Charge) amount = payment.fee;
          else if (payment.direction === DepositPaymentDirectionEnum.Outgoing) amount = payment.senderAmount;
          else amount = payment.beneficiaryAmount;
          return [
            {
              type: TableCellType.Custom,
              value: (
                <div className={cn('truncate px-2 py-1 text-center font-normal', color.bg, color.text)}>
                  {t(`statuses.paymentTransaction.${payment.status}`)}
                </div>
              ),
            },
            { type: TableCellType.Text, value: t(`direction.${payment.direction.toLowerCase()}`) },
            { type: TableCellType.Text, value: DateUtils.formatDate(payment.valueDate) },
            { type: TableCellType.Text, value: payment.referenceNumber, width: '150px' },
            { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(amount) },
            {
              type: TableCellType.Text,
              value: payment.direction === DepositPaymentDirectionEnum.Outgoing ? payment.currencyIn : payment.currencyOut,
            },
            { type: TableCellType.Text, value: payment.beneficiaryIban, className: '[direction:rtl]' },
            { type: TableCellType.Text, value: payment.senderIban, className: '[direction:rtl]' },
            { type: TableCellType.Text, value: payment.description },
            { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(payment.totalAmount, payment.currency) },
          ];
        } else {
          const transaction = row.transaction;
          const color = ColorUtils.getTransactionStatusStyles(transaction.status);
          return [
            {
              type: TableCellType.Custom,
              value: (
                <div className={cn('truncate px-2 py-1 text-center font-normal', color.bg, color.text)}>
                  {t(`statuses.cardTransaction.${transaction.status}`)}
                </div>
              ),
            },
            { type: TableCellType.Text, value: t(`direction.${transaction.direction.toLowerCase()}`) },
            { type: TableCellType.Text, value: DateUtils.formatDate(transaction.date) },
            { type: TableCellType.Text, value: transaction.ref, width: '150px' },
            { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(transaction.amount) },
            { type: TableCellType.Text, value: transaction.currency },
            {
              type: TableCellType.Text,
              value: transaction.direction === TransactionDirection.Outgoing ? transaction.merchant : transaction.cardNumber,
              className: '[direction:rtl]',
            },
            {
              type: TableCellType.Text,
              value: transaction.direction === TransactionDirection.Outgoing ? transaction.cardNumber : transaction.merchant,
              className: '[direction:rtl]',
            },
            { type: TableCellType.Text, value: transaction.description },
            { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(transaction.totalAmount, transaction.currency) },
          ];
        }
      })}
      pagination={{
        currentPage: props.currentPage,
        totalItemCount: props.totalItemCount,
        countItemPage: props.countItemPage,
        onChangePage: props.onChangePage,
        onChangeCountItemPage: props.onChangeCountItemPage,
      }}
      isRowHoverable={(rowIndex) => {
        const row = props.data[rowIndex];
        if (row.dataType !== TransactionDataType.Payment) return false;
        return PermissionUtils.viewAccountTransactionDetails(row.payment.accId);
      }}
      onClick={(rowIndex) => {
        const row = props.data[rowIndex];
        if (row.dataType !== TransactionDataType.Payment) return;
        navigate(RoutePaths.accountTransaction.onNavigate(row.payment.id));
      }}
    />
  );
};
